import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import { useParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { fetchAPI } from "../config/api";
import { isBattingTab } from "../utils/playerDataUtils";
import { trackPlayerPageVisit } from "../services/dataService";
import { PercentileSection } from "../components/player/PercentileRankings";
import StatTable from "../components/player/StatTable";
import PlayerHeader from "../components/player/PlayerHeader";
import SprayChart from "../components/player/SprayChart";
import { PositionChart } from "../components/player/PositionChart";
import { RollingChart } from "../components/player/RollingChart";
import { SimilarBatters } from "../components/player/SimilarBatters";
import { SimilarPitchers } from "../components/player/SimilarPitchers";
import { usePercentiles } from "../hooks/usePercentiles";
import { DEFAULT_DIVISION, toDivisionApi } from "../config/constants";

const STAT_TYPES = {
  BATTING: "batting",
  PITCHING: "pitching",
  BASERUNNING: "baserunning",
  BATTED_BALL: "batted_ball",
  BATTED_BALL_PITCHER: "batted_ball_pitcher",
  SITUATIONAL: "situational",
  SITUATIONAL_PITCHER: "situational_pitcher",
  SPLITS: "splits",
  SPLITS_PITCHER: "splits_pitcher",
  VALUE_PITCHER: "value_pitcher",
};

const TAB_FETCH_TYPES = new Set([
  STAT_TYPES.BASERUNNING,
  STAT_TYPES.BATTED_BALL,
  STAT_TYPES.BATTED_BALL_PITCHER,
  STAT_TYPES.SITUATIONAL,
  STAT_TYPES.SITUATIONAL_PITCHER,
  STAT_TYPES.SPLITS,
  STAT_TYPES.SPLITS_PITCHER,
  STAT_TYPES.VALUE_PITCHER,
]);

const createInitialTabStats = () => {
  const init = {};
  for (const k of TAB_FETCH_TYPES) init[k] = { status: "idle", data: [] };
  return init;
};

const getYear = (s) => s?.year ?? null;
const getDivision = (s) => s?.division ?? null;

const LoadingState = () => (
  <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden">
    <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
      <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
    </div>
  </div>
);

const ErrorState = memo(({ error }) => (
  <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden">
    <div className="relative z-10 flex items-center justify-center min-h-screen">
      <div className="bg-white/70 backdrop-blur-lg border border-white/20 rounded-2xl shadow-xl p-8 max-w-md">
        <div className="flex items-center gap-3 mb-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold text-red-800">
            Error Loading Player
          </h3>
        </div>
        <p className="text-red-600">{error}</p>
      </div>
    </div>
  </div>
));
ErrorState.displayName = "ErrorState";

const TabButton = memo(({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 font-medium text-xs rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
      active ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:text-blue-600"
    }`}
    style={{ minWidth: "max-content", padding: "0.5rem 0.75rem" }}
  >
    {children}
  </button>
));
TabButton.displayName = "TabButton";

const TabNavigation = memo(({ activeTab, onTabChange, availableTabs }) => (
  <div className="border-b border-gray-100">
    <div className="px-4 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
      {availableTabs.map((tab) => (
        <TabButton
          key={tab.id}
          active={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
        </TabButton>
      ))}
    </div>
  </div>
));
TabNavigation.displayName = "TabNavigation";

const renderTeamName = (teamName) => (
  <div className="w-full flex justify-center items-center">
    <span className="text-sm font-medium text-gray-700">{teamName}</span>
  </div>
);

const enrichStats = (stats) => {
  if (!stats || !Array.isArray(stats)) return [];
  const out = stats.map((stat) => ({
    ...stat,
    renderedTeam: renderTeamName(stat.team_name),
    renderedConference: renderTeamName(stat.conference),
  }));
  out.sort((a, b) => (getYear(b) ?? 0) - (getYear(a) ?? 0));
  return out;
};

const getBaseStatType = (statType) => {
  const battingTabs = [
    STAT_TYPES.BATTING,
    STAT_TYPES.BASERUNNING,
    STAT_TYPES.BATTED_BALL,
    STAT_TYPES.SPLITS,
    STAT_TYPES.SITUATIONAL,
  ];
  
  if (battingTabs.includes(statType)) {
    return STAT_TYPES.BATTING;
  }
  
  return STAT_TYPES.PITCHING;
};

const PlayerContent = memo(
  ({
    activeTab,
    statCategories,
    selectedDivision,
    playerId,
    playerData,
    selectedYear,
  }) => {
    const [isRollingChartLoading, setIsRollingChartLoading] = useState({
      batter: false,
      pitcher: false,
    });

    const stats = useMemo(() => {
      return statCategories[activeTab]?.stats ?? [];
    }, [statCategories, activeTab]);
    
    const statType = statCategories[activeTab]?.type || activeTab;

    const playerType = useMemo(() => {
      return [
        STAT_TYPES.BATTING,
        STAT_TYPES.BASERUNNING,
        STAT_TYPES.SITUATIONAL,
        STAT_TYPES.SPLITS,
        STAT_TYPES.BATTED_BALL,
      ].includes(activeTab)
        ? "batter"
        : "pitcher";
    }, [activeTab]);

    const currentYear = useMemo(() => {
      // prefer selectedYear (controlled by percentiles / dropdown)
      if (selectedYear) return selectedYear;
      // fallback: max year from the table we’re showing
      if (!stats?.length) return new Date().getFullYear();
      return Math.max(...stats.map((s) => getYear(s) ?? 0));
    }, [selectedYear, stats]);

    const playerName = playerData?.player_name || "";
    const chartLabel = playerType === "batter" ? "Batting wOBA" : "Pitching wOBA";

    const handleRollingChartLoadingState = useCallback((type, isLoading) => {
      setIsRollingChartLoading((prev) => ({ ...prev, [type]: isLoading }));
    }, []);


    const shouldShowSprayChart =
      [
        STAT_TYPES.BATTING,
        STAT_TYPES.BASERUNNING,
        STAT_TYPES.SITUATIONAL,
        STAT_TYPES.SPLITS,
        STAT_TYPES.BATTED_BALL,
      ].includes(activeTab) && stats.length > 0;

    const shouldShowSimilarBatters = shouldShowSprayChart;
    const shouldShowSimilarPitchers = playerType === "pitcher" && stats.length > 0;

    return (
      <div className="p-6 space-y-8">
        {stats.length > 0 && <StatTable stats={stats} type={statType} />}

        {shouldShowSimilarPitchers && (
          <SimilarPitchers
            playerId={playerId}
            year={currentYear}
            division={selectedDivision}
          />
        )}

        {playerType === "pitcher" && stats.length > 0 && (
          <div>
            {isRollingChartLoading.pitcher ? (
              <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-sm">
                <div className="w-8 h-8 border-3 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            ) : (
              <RollingChart
                playerId={playerId}
                playerType={playerType}
                window={25}
                playerName={playerName}
                chartTitle={chartLabel}
                onLoadingStateChange={(isLoading) =>
                  handleRollingChartLoadingState("pitcher", isLoading)
                }
              />
            )}
          </div>
        )}

        {shouldShowSimilarBatters && (
          <SimilarBatters
            playerId={playerId}
            year={currentYear}
            division={selectedDivision}
          />
        )}

        {shouldShowSprayChart && (
          <SprayChart
            playerId={playerId}
            year={currentYear}
            division={selectedDivision}
          />
        )}

        {playerType === "batter" && shouldShowSprayChart && (
          <PositionChart
            playerId={playerId}
            year={currentYear}
            division={selectedDivision}
          />
        )}

        {playerType === "batter" && shouldShowSprayChart && (
          <div>
            {isRollingChartLoading.batter ? (
              <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-sm mt-4">
                <div className="w-8 h-8 border-3 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            ) : (
              <RollingChart
                playerId={playerId}
                playerType={playerType}
                window={25}
                playerName={playerName}
                chartTitle={chartLabel}
                onLoadingStateChange={(isLoading) =>
                  handleRollingChartLoadingState("batter", isLoading)
                }
              />
            )}
          </div>
        )}
      </div>
    );
  }
);
PlayerContent.displayName = "PlayerContent";

export const PlayerPage = () => {
  const { playerId } = useParams();

  const decodedPlayerId = useMemo(() => decodeURIComponent(playerId), [playerId]);

  const [playerData, setPlayerData] = useState(null);
  const [playerYears, setPlayerYears] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderReady, setIsHeaderReady] = useState(false);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState(STAT_TYPES.BATTING);
  const [selectedDivision, setSelectedDivision] = useState(DEFAULT_DIVISION);
  const [selectedConference, setSelectedConference] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  // ✅ Store per-tab fetch state to support lazy loading
  const [tabStats, setTabStats] = useState(createInitialTabStats);

  const fallbackYearsByTab = useMemo(() => {
    const b = (playerData?.battingStats || []).map(getYear).filter(Boolean);
    const p = (playerData?.pitchingStats || []).map(getYear).filter(Boolean);
    const sortedB = [...new Set(b)].sort((a, b) => b - a);
    const sortedP = [...new Set(p)].sort((a, b) => b - a);
    return {
      batting_years: sortedB,
      pitching_years: sortedP,
      most_recent_batting: sortedB[0] || null,
      most_recent_pitching: sortedP[0] || null,
    };
  }, [playerData]);

  const {
    percentiles,
    isLoading: isPercentilesLoading,
    targetYear: percentTargetYear,
    prefetchYears,
  } = usePercentiles({
    playerId: decodedPlayerId,
    playerYears,
    activeTab,
    division: selectedDivision,
    conference: selectedConference,
    selectedYear,
    fallbackYearsByTab,
  });

  const enhancePlayerData = useCallback((data) => {
    if (!data) return null;

    const battingStats = enrichStats(data.batting_stats);
    const pitchingStats = enrichStats(data.pitching_stats);

    // ✅ yearsPlayed must handle Season/Year too
    const yearsPlayed = [
      ...new Set([
        ...(battingStats || []).map(getYear),
        ...(pitchingStats || []).map(getYear),
      ].filter(Boolean)),
    ].sort((a, b) => b - a);

    return {
      ...data,
      renderedTeam: data.current_team ? renderTeamName(data.current_team) : null,
      renderedConference: data.conference ? renderTeamName(data.conference) : null,
      battingStats,
      pitchingStats,
      yearsPlayed,
    };
  }, []);

  const determineDivision = useCallback((enhanced) => {
    const all = [...(enhanced?.battingStats || []), ...(enhanced?.pitchingStats || [])];
    if (!all.length) return DEFAULT_DIVISION;

    // pick stat row from most recent year that actually has a division
    const byYearDesc = [...all].sort((a, b) => (getYear(b) ?? 0) - (getYear(a) ?? 0));
    for (const s of byYearDesc) {
      const d = getDivision(s);
      if (d) return toDivisionApi(d);
    }
    return DEFAULT_DIVISION;
  }, []);

  const determineInitialActiveTab = useCallback((enhanced) => {
    if (!enhanced?.battingStats?.length && enhanced?.pitchingStats?.length) return STAT_TYPES.PITCHING;
    return STAT_TYPES.BATTING;
  }, []);

  const fetchPlayerYears = useCallback(
    async (division, signal) => {
      const url = `/api/player-years/${decodedPlayerId}/${division}`;
      return fetchAPI(url, { signal });
    },
    [decodedPlayerId]
  );

  const fetchStatsForType = useCallback(
    async (statType, division, signal) => {
      const url = `/api/leaderboards/${statType}/${encodeURIComponent(
        decodedPlayerId
      )}?start_year=2021&end_year=2025&division=${division}`;
      const response = await fetchAPI(url, { signal });
      return enrichStats(response);
    },
    [decodedPlayerId]
  );

  // ✅ Lazy loader: only fetch tab stats when needed
  const ensureTabLoaded = useCallback(
    async (tab, division, signal) => {
      if (!TAB_FETCH_TYPES.has(tab)) return;
      setTabStats((prev) => {
        const cur = prev[tab];
        if (!cur || cur.status === "idle" || cur.status === "error") {
          return { ...prev, [tab]: { ...cur, status: "loading" } };
        }
        return prev;
      });

      try {
        const data = await fetchStatsForType(tab, division, signal);
        if (signal?.aborted) return;
        setTabStats((prev) => ({ ...prev, [tab]: { status: "ready", data } }));
      } catch (e) {
        if (e?.name === "AbortError") return;
        setTabStats((prev) => ({ ...prev, [tab]: { status: "error", data: [] } }));
      }
    },
    [fetchStatsForType]
  );

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setPlayerData(null);
    setPlayerYears(null);
    setIsHeaderReady(false);
    setSelectedConference(null);
    setSelectedYear(null);
    setTabStats(createInitialTabStats());
  }, [decodedPlayerId]);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchAPI(`/api/player/${decodedPlayerId}`, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        const enhanced = enhancePlayerData(response);
        setPlayerData(enhanced);

        const division = determineDivision(enhanced);
        setSelectedDivision(division);

        const initialTab = determineInitialActiveTab(enhanced);
        setActiveTab(initialTab);

        const initialYear = enhanced?.yearsPlayed?.[0] ?? null;
        if (initialYear) setSelectedYear(initialYear);

        const [yearsResp] = await Promise.all([
          fetchPlayerYears(division, controller.signal).catch(() => null),
          ...Array.from(TAB_FETCH_TYPES).map(tab => 
            ensureTabLoaded(tab, division, controller.signal).catch(() => null)
          ),
        ]);

        if (!controller.signal.aborted) {
          if (yearsResp) setPlayerYears(yearsResp);
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          setError(e?.message || "Failed to load player data");
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    decodedPlayerId,
    enhancePlayerData,
    determineDivision,
    determineInitialActiveTab,
    fetchPlayerYears,
    ensureTabLoaded,
  ]);

  // ✅ When tab changes, lazy fetch if needed
  useEffect(() => {
    if (!playerData) return;
    const controller = new AbortController();
    ensureTabLoaded(activeTab, selectedDivision, controller.signal);
    return () => controller.abort();
  }, [activeTab, selectedDivision, playerData, ensureTabLoaded]);

  useEffect(() => {
    if (percentTargetYear && selectedYear !== percentTargetYear) {
      setSelectedYear(percentTargetYear);
    }
  }, [percentTargetYear, selectedYear]);

  useEffect(() => {
    if (!playerYears) return;
    const years = isBattingTab(activeTab)
      ? playerYears?.batting_years
      : playerYears?.pitching_years;
    if (!years?.length) return;
    const recent = [...years].sort((a, b) => b - a).slice(0, 3);
    prefetchYears(recent);
  }, [playerYears, activeTab, prefetchYears]);

  useEffect(() => {
    if (playerData) {
      trackPlayerPageVisit(playerData.player_id, playerData.player_name);
    }
  }, [playerData]);

  const handleYearChange = useCallback((year) => setSelectedYear(year), []);
  const handleConferenceChange = useCallback((conf) => setSelectedConference(conf), []);

  const handleTabChange = useCallback(
    (newTab) => {
      if (newTab === activeTab) return;

      setActiveTab(newTab);

      if (playerYears) {
        const newTabType = isBattingTab(newTab) ? "batting" : "pitching";
        const mostRecentYear =
          newTabType === "batting"
            ? playerYears.most_recent_batting
            : playerYears.most_recent_pitching;

        // only update if we actually have a meaningful year
        if (mostRecentYear && mostRecentYear !== selectedYear) {
          setSelectedYear(mostRecentYear);
        }
      }
    },
    [activeTab, playerYears, selectedYear]
  );

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (playerData?.battingStats?.length) tabs.push({ id: STAT_TYPES.BATTING, label: "Batting" });
    if (playerData?.pitchingStats?.length) tabs.push({ id: STAT_TYPES.PITCHING, label: "Pitching" });

    const addIfAny = (id, label) => {
      const st = tabStats[id];
      if (!st) return;
      
      if (st.status === "ready") {
        if (st.data.length > 0) {
          tabs.push({ id, label });
        }
      } else if (st.status === "loading" || st.status === "idle") {
        tabs.push({ id, label });
      }
    };

    addIfAny(STAT_TYPES.BASERUNNING, "Baserunning");
    addIfAny(STAT_TYPES.BATTED_BALL, "Batted Ball (Batting)");
    addIfAny(STAT_TYPES.BATTED_BALL_PITCHER, "Batted Ball (Pitching)");
    addIfAny(STAT_TYPES.SITUATIONAL, "Situational (Batting)");
    addIfAny(STAT_TYPES.SITUATIONAL_PITCHER, "Situational (Pitching)");
    addIfAny(STAT_TYPES.SPLITS, "Splits (Batting)");
    addIfAny(STAT_TYPES.SPLITS_PITCHER, "Splits (Pitching)");
    addIfAny(STAT_TYPES.VALUE_PITCHER, "Value (Pitching)");
    return tabs;
  }, [playerData, tabStats]);

  const statCategories = useMemo(() => {
    const getTabStats = (tab) => (TAB_FETCH_TYPES.has(tab) ? tabStats[tab]?.data || [] : []);
    return {
      [STAT_TYPES.BATTING]: { stats: playerData?.battingStats || [], type: STAT_TYPES.BATTING },
      [STAT_TYPES.PITCHING]: { stats: playerData?.pitchingStats || [], type: STAT_TYPES.PITCHING },
      [STAT_TYPES.BASERUNNING]: { stats: getTabStats(STAT_TYPES.BASERUNNING), type: STAT_TYPES.BASERUNNING },
      [STAT_TYPES.BATTED_BALL]: { stats: getTabStats(STAT_TYPES.BATTED_BALL), type: STAT_TYPES.BATTED_BALL },
      [STAT_TYPES.BATTED_BALL_PITCHER]: { stats: getTabStats(STAT_TYPES.BATTED_BALL_PITCHER), type: STAT_TYPES.BATTED_BALL_PITCHER },
      [STAT_TYPES.SITUATIONAL]: { stats: getTabStats(STAT_TYPES.SITUATIONAL), type: STAT_TYPES.SITUATIONAL },
      [STAT_TYPES.SITUATIONAL_PITCHER]: { stats: getTabStats(STAT_TYPES.SITUATIONAL_PITCHER), type: STAT_TYPES.SITUATIONAL_PITCHER },
      [STAT_TYPES.SPLITS]: { stats: getTabStats(STAT_TYPES.SPLITS), type: STAT_TYPES.SPLITS },
      [STAT_TYPES.SPLITS_PITCHER]: { stats: getTabStats(STAT_TYPES.SPLITS_PITCHER), type: STAT_TYPES.SPLITS_PITCHER },
      [STAT_TYPES.VALUE_PITCHER]: { stats: getTabStats(STAT_TYPES.VALUE_PITCHER), type: STAT_TYPES.VALUE_PITCHER },
    };
  }, [playerData, tabStats]);

  const enhancedPlayerDataForPercentile = useMemo(() => {
    if (!playerData) return null;

    const yearsPlayed = [
      ...new Set([
        ...(playerData?.battingStats || []).map(getYear),
        ...(playerData?.pitchingStats || []).map(getYear),
      ].filter(Boolean)),
    ].sort((a, b) => b - a);

    const divisionsPlayed = [
      ...new Set([
        ...(playerData?.battingStats || []).map(getDivision),
        ...(playerData?.pitchingStats || []).map(getDivision),
      ].filter(Boolean)),
    ].sort((a, b) => a - b);

    return { ...playerData, yearsPlayed, divisionsPlayed, battingStats: playerData.battingStats, pitchingStats: playerData.pitchingStats };
  }, [playerData]);

  const currentPercentiles = useMemo(() => {
    const baseType = getBaseStatType(activeTab);
    return { [baseType]: percentiles[baseType] };
  }, [activeTab, percentiles]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (!playerData) return <LoadingState />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="relative z-10 container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        {!isHeaderReady && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_5fr] gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <PlayerHeader playerData={playerData} onReady={() => setIsHeaderReady(true)} />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <PercentileSection
                playerData={{ ...enhancedPlayerDataForPercentile, playerYears }}
                initialPercentiles={currentPercentiles}
                activeTab={activeTab}
                selectedYear={selectedYear}
                onYearChange={handleYearChange}
                onConferenceChange={handleConferenceChange}
                isLoading={isPercentilesLoading}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} availableTabs={availableTabs} />

            <PlayerContent
              key={decodedPlayerId}
              activeTab={activeTab}
              statCategories={statCategories}
              selectedDivision={selectedDivision}
              playerId={decodedPlayerId}
              playerData={playerData}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
